import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useRadiologyTests, useCreateRadiologyTest, useUpdateRadiologyTest, useDeleteRadiologyTest } from '../../hooks/useRadiologyTests';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { RadiologyTestForm } from '../../components/radiologyTests/RadiologyTestForm';
import { Table } from '../../components/common/Table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { RadiologyTest } from '../../types/api.types';
import { CreateRadiologyTestDto, UpdateRadiologyTestDto } from '../../api/radiologyTests.api';
import toast from 'react-hot-toast';

export const RadiologyTestsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRadiologyTest, setEditingRadiologyTest] = useState<RadiologyTest | null>(null);
  const [deleteRadiologyTestId, setDeleteRadiologyTestId] = useState<number | null>(null);

  const { data: radiologyTests, isLoading, error } = useRadiologyTests();
  const createMutation = useCreateRadiologyTest();
  const updateMutation = useUpdateRadiologyTest();
  const deleteMutation = useDeleteRadiologyTest();

  const handleCreate = async (data: CreateRadiologyTestDto) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('radiologyTests.radiologyTestCreated'));
      setIsModalOpen(false);
    } catch (error) {
      toast.error(t('radiologyTests.radiologyTestCreatedError'));
    }
  };

  const handleUpdate = async (data: CreateRadiologyTestDto | UpdateRadiologyTestDto) => {
    if (!editingRadiologyTest) return;
    const updateData = data as UpdateRadiologyTestDto;

    try {
      await updateMutation.mutateAsync({ id: editingRadiologyTest.id, data: updateData });
      toast.success(t('radiologyTests.radiologyTestUpdated'));
      setIsModalOpen(false);
      setEditingRadiologyTest(null);
    } catch (error) {
      toast.error(t('radiologyTests.radiologyTestUpdatedError'));
    }
  };

  const handleDelete = async () => {
    if (!deleteRadiologyTestId) return;
    
    try {
      await deleteMutation.mutateAsync(deleteRadiologyTestId);
      toast.success(t('radiologyTests.radiologyTestDeleted'));
      setDeleteRadiologyTestId(null);
    } catch (error) {
      toast.error(t('radiologyTests.radiologyTestDeletedError'));
    }
  };

  const handleEdit = (radiologyTest: RadiologyTest) => {
    setEditingRadiologyTest(radiologyTest);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRadiologyTest(null);
  };

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('radiologyTests.title')}
        actions={
          <Button
            variant="primary"
            onClick={() => setIsModalOpen(true)}
          >
            {isRTL ? (
              <>
                <Plus className="ms-2" size={16} />
                {t('radiologyTests.addRadiologyTest')}
              </>
            ) : (
              <>
                <Plus className="me-2" size={16} />
                {t('radiologyTests.addRadiologyTest')}
              </>
            )}
          </Button>
        }
      />

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {error ? (
                <div className="p-4">
                  <ErrorMessage message={error.message || t('errors.required')} />
                </div>
              ) : (
                <Table
                  data={Array.isArray(radiologyTests) ? radiologyTests : []}
                  columns={[
                    {
                      key: 'name',
                      header: t('radiologyTests.name'),
                      render: (radiologyTest) => (
                        <span className="fw-semibold text-dark">{radiologyTest.name}</span>
                      ),
                      sortable: true,
                      width: '40%',
                    },
                    {
                      key: 'description',
                      header: t('radiologyTests.description'),
                      render: (radiologyTest) => (
                        <span className="text-muted">{radiologyTest.description || '-'}</span>
                      ),
                      sortable: true,
                      width: '45%',
                    },
                    {
                      key: 'actions',
                      header: t('common.actions'),
                      render: (radiologyTest) => (
                        <div className={`d-flex gap-2 ${isRTL ? 'justify-content-start' : 'justify-content-end'}`}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleEdit(radiologyTest);
                            }}
                            className="p-2"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              setDeleteRadiologyTestId(radiologyTest.id);
                            }}
                            className="p-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ),
                      width: '15%',
                    },
                  ]}
                  keyExtractor={(radiologyTest) => radiologyTest.id}
                  emptyMessage={t('radiologyTests.noRadiologyTests')}
                  pageSize={15}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingRadiologyTest ? t('radiologyTests.editRadiologyTest') : t('radiologyTests.addRadiologyTest')}
      >
        <RadiologyTestForm
          radiologyTest={editingRadiologyTest || undefined}
          onSubmit={editingRadiologyTest ? handleUpdate : handleCreate}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onCancel={handleCloseModal}
        />
      </Modal>

      <ConfirmDialog
        isOpen={deleteRadiologyTestId !== null}
        onClose={() => setDeleteRadiologyTestId(null)}
        onConfirm={handleDelete}
        title={t('common.delete')}
        message={t('radiologyTests.deleteConfirm')}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};





